import React, { useMemo } from 'react';
import { Transaction, Period, TransactionType } from '../types';
import { BalanceCard } from '../components/BalanceCard';
import { TransactionList } from '../components/TransactionList';
import { PeriodToggle } from '../components/PeriodToggle';
import { DateNavigation } from '../components/DateNavigation';
import { StatCard } from '../components/StatCard';
import { TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { isSameDay, isSameMonth, isSameYear, startOfYear, endOfYear, eachMonthOfInterval, format } from 'date-fns';
interface HomePageProps {
  transactions: Transaction[];
  period: Period;
  date: Date;
  onPeriodChange: (p: Period) => void;
  onDateChange: (d: Date) => void;
  onEditTransaction: (t: Transaction) => void;
  onViewAll: () => void;
  onViewByType: (type: TransactionType) => void;
}
export function HomePage({
  transactions,
  period,
  date,
  onPeriodChange,
  onDateChange,
  onEditTransaction,
  onViewAll,
  onViewByType
}: HomePageProps) {
  const totalBalance = useMemo(() => {
    return transactions.reduce((acc, t) => {
      if (t.affectsBalance === false) return acc;
      if (t.type === 'income') return acc + t.amount;
      if (t.type === 'expense') return acc - t.amount;
      if (t.type === 'savings') return acc - t.amount;
      return acc;
    }, 0);
  }, [transactions]);
  const totalSavings = useMemo(() => {
    return transactions.filter(t => t.type === 'savings').reduce((sum, t) => sum + t.amount, 0);
  }, [transactions]);
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      const tDate = new Date(t.date);
      if (period === 'day') return isSameDay(tDate, date);
      if (period === 'month') return isSameMonth(tDate, date);
      return isSameYear(tDate, date);
    });
  }, [transactions, period, date]);
  const stats = useMemo(() => {
    return filteredTransactions.reduce((acc, t) => {
      if (t.type === 'income') acc.income += t.amount;
      if (t.type === 'expense') acc.expenses += t.amount;
      return acc;
    }, {
      income: 0,
      expenses: 0
    });
  }, [filteredTransactions]);
  const recentTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  }, [transactions]);
  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const yearMonths = useMemo(() => {
    if (period !== 'year') return [];
    const start = startOfYear(date);
    const end = endOfYear(date);
    const months = eachMonthOfInterval({
      start,
      end
    });
    return months.map(month => {
      const monthTrans = transactions.filter(t => isSameMonth(new Date(t.date), month));
      const income = monthTrans.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
      const expenses = monthTrans.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
      const savings = monthTrans.filter(t => t.type === 'savings').reduce((sum, t) => sum + t.amount, 0);
      return {
        name: format(month, 'MMM'),
        income,
        expenses,
        savings,
        balance: income - expenses
      };
    });
  }, [period, date, transactions]);
  const yearSavingsProgress = useMemo(() => {
    if (period !== 'year') return [];
    return yearMonths.map((m, i) => ({
      month: m.name,
      total: yearMonths.slice(0, i + 1).reduce((sum, month) => sum + month.savings, 0)
    }));
  }, [period, yearMonths]);
  const handlePrevDate = () => {
    const newDate = new Date(date);
    if (period === 'day') newDate.setDate(date.getDate() - 1);
    if (period === 'month') newDate.setMonth(date.getMonth() - 1);
    if (period === 'year') newDate.setFullYear(date.getFullYear() - 1);
    onDateChange(newDate);
  };
  const handleNextDate = () => {
    const newDate = new Date(date);
    if (period === 'day') newDate.setDate(date.getDate() + 1);
    if (period === 'month') newDate.setMonth(date.getMonth() + 1);
    if (period === 'year') newDate.setFullYear(date.getFullYear() + 1);
    onDateChange(newDate);
  };
  return <div className="space-y-6 pb-24" data-id="element-359">
      <div className="flex flex-col gap-4" data-id="element-360">
        <BalanceCard balance={totalBalance} savings={totalSavings} data-id="element-361" />

        <div className="bg-stone-900 sticky top-0 z-30 py-2 border-b border-stone-800/50 backdrop-blur-md bg-opacity-80" data-id="element-362">
          <PeriodToggle value={period} onChange={onPeriodChange} data-id="element-363" />
          <DateNavigation date={date} period={period} onPrev={handlePrevDate} onNext={handleNextDate} onDateClick={() => {}} onDateChange={onDateChange} data-id="element-364" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3" data-id="element-365">
        <div onClick={() => onViewByType('income')} className="cursor-pointer" data-id="element-366">
          <StatCard title="Income" value={formatMoney(stats.income)} icon={TrendingUp} color="emerald" data-id="element-367" />
        </div>
        <div onClick={() => onViewByType('expense')} className="cursor-pointer" data-id="element-368">
          <StatCard title="Expenses" value={formatMoney(stats.expenses)} icon={TrendingDown} color="red" data-id="element-369" />
        </div>
      </div>

      {period === 'year' && <>
          <div className="bg-stone-800 rounded-3xl p-6 border border-stone-700/30" data-id="element-370">
            <h3 className="text-stone-50 font-bold mb-4" data-id="element-371">Monthly Breakdown</h3>
            <div className="space-y-4" data-id="element-372">
              {yearMonths.map(m => <div key={m.name} className="flex items-center justify-between text-sm" data-id="element-373">
                  <span className="text-stone-400 w-12 font-medium" data-id="element-374">
                    {m.name}
                  </span>
                  <div className="flex-1 mx-4 h-2 bg-stone-700 rounded-full overflow-hidden flex" data-id="element-375">
                    <div className="bg-emerald-500 h-full" style={{
                width: `${m.income / (m.income + m.expenses || 1) * 100}%`
              }} data-id="element-376" />
                    <div className="bg-red-500 h-full" style={{
                width: `${m.expenses / (m.income + m.expenses || 1) * 100}%`
              }} data-id="element-377" />
                  </div>
                  <span className="text-stone-200 font-mono text-xs" data-id="element-378">
                    {formatMoney(m.balance)}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-900/50 to-stone-800 rounded-3xl p-6 border border-emerald-900/30" data-id="element-379">
            <h3 className="text-emerald-50 font-bold mb-4 flex items-center gap-2" data-id="element-380">
              <Wallet size={20} className="text-emerald-400" data-id="element-381" />
              Savings Progress
            </h3>
            <div className="space-y-3" data-id="element-382">
              {yearSavingsProgress.map((item, i) => {
            const maxSavings = Math.max(...yearSavingsProgress.map(s => s.total), 1);
            const percentage = item.total / maxSavings * 100;
            return <div key={item.month} className="flex items-center gap-3" data-id="element-383">
                    <span className="text-stone-400 text-xs w-8" data-id="element-384">
                      {item.month}
                    </span>
                    <div className="flex-1 h-8 bg-stone-900/50 rounded-xl overflow-hidden" data-id="element-385">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-xl flex items-center justify-end pr-2 transition-all duration-500" style={{
                  width: `${percentage}%`
                }} data-id="element-386">
                        {item.total > 0 && <span className="text-white text-xs font-bold" data-id="element-387">
                            {formatMoney(item.total)}
                          </span>}
                      </div>
                    </div>
                  </div>;
          })}
            </div>
            <div className="mt-4 pt-4 border-t border-emerald-900/30" data-id="element-388">
              <div className="flex justify-between items-center" data-id="element-389">
                <span className="text-emerald-200/70 text-sm" data-id="element-390">
                  Total Saved This Year
                </span>
                <span className="text-2xl font-bold text-emerald-50" data-id="element-391">
                  {formatMoney(yearSavingsProgress[yearSavingsProgress.length - 1]?.total || 0)}
                </span>
              </div>
            </div>
          </div>
        </>}

      {period !== 'year' && <div data-id="element-392">
          <div className="flex items-center justify-between mb-4 px-2" data-id="element-393">
            <h3 className="text-stone-50 font-bold text-lg" data-id="element-394">Recent Activity</h3>
            <button onClick={onViewAll} className="text-amber-500 text-sm font-medium hover:text-amber-400" data-id="element-395">
              View All
            </button>
          </div>
          <TransactionList transactions={recentTransactions} onEdit={onEditTransaction} data-id="element-396" />
        </div>}
    </div>;
}